import {
  createChatbotConfig,
  createChatbotEvents,
  InstagramWebhookEvent,
  MessengerWebhookEvent,
} from '@play-co/replicant';
import { stateSchema } from '../State';
import { chatbotMessageTemplates } from './messageTemplates';
import messages from '../messages';
import scheduledActions from '../scheduledActions';
import computedProperties from '../computedProperties';
import { asyncGetters } from '../asyncGetters';
import {
  scheduleQuizEggChat,
  scheduleStatusUpdateMessage,
} from '../buruburu/petChatbot/petChatbot.scheduledActions';
import { scheduleEnergyRefilledMessage } from '../buruburu/inventory/inventory.scheduledActions';
import { Pet } from '../buruburu/pet';
import { Inventory } from '../buruburu/inventory';
import { getLocale } from '../getters/user';
import { Visit } from '../visit';
import { asyncEndVisitAction } from '../visit/visit.actions';
import { VisitRecord } from '../visit/record';
import ab, { getDynamicBucketID } from '../ruleset/ab';
import { decode, encode } from 'src/replicant/utils/base64';
import { processQuickReplyAction } from './chatbotConversation';
import { decodeQuickReplyPayload } from './chatbot.utils';
import { createLoginToken, generateChatbotUrl } from './chatbotMessage';
import { FBClient } from '../utils/fb';

export interface LinkRefData {
  type: 'SHARE' | 'LANDING';

  /** GCInstant payload that should get passed through to the final Play button sent to the user. */
  payload?: Record<string, unknown>;

  messageText?: string;
  imageUrl?: string;
  buttonText?: string;
}

export interface PlayButtonOptions {
  messageText?: string;
  messageSubtitle?: string;
  imageUrl?: string;
  payload?: any;
  buttonText?: string;
  action?: string;
  species?: string;
}

export function extractAssetIdFromAttachments(
  attachments: string,
): string | undefined {
  let url: URL;
  try {
    url = new URL(JSON.parse(attachments)[0].payload.url);
  } catch {
    console.warn('Error parsing message attachments URL');
    return undefined;
  }
  return url.searchParams.get('asset_id');
}

const events = createChatbotEvents(stateSchema, {
  messages,
  scheduledActions,
  computedProperties,
  asyncGetters,
})({
  onGameEnd: async (state, data, api, isFirstInSession) => {
    // Abort onGameEnd if is not the first time in session
    if (!isFirstInSession) return;

    // Abort if user's platform is not set, otherwise chatbot messages will break
    if (!state.platform) return;

    const now = api.date.now();

    const { getCurrentEvolution, getNextPetState } = Pet.getters(state);

    scheduleStatusUpdateMessage(
      {
        nextPetState: getNextPetState(now),
        now,
      },
      api,
    );

    const { getEnergy, getTimeToFullEnergy } = Inventory.getters(state);

    scheduleEnergyRefilledMessage(
      getCurrentEvolution(),
      getEnergy(now),
      getTimeToFullEnergy(now),
      getLocale(state),
      state,
      api,
    );

    if (Visit.getters(state).isVisiting()) {
      await asyncEndVisitAction(
        () => api.postMessage.deleteVisitSession(api.getUserID(), {}),
        state,
        api,
        { VisitRecord, Pet },
      ).catch((e) => api.reportError(e));
    }

    // Send message to update online status
    api.postMessage.setOnline(api.getUserID(), false);
  },

  onWebhook: async (state, event, api) => {
    function trackEvent(
      eventType: string,
      eventProperties: Record<string, unknown> = {},
    ) {
      api.sendAnalyticsEvents([
        {
          userId: api.getUserID(),
          eventType,
          eventProperties,
          userProperties: {
            $firstEntryAdId: state.firstChatbotAdReferral?.adId,
            $firstEntryAdTitle: state.firstChatbotAdReferral?.adTitle,
            $firstEntryAdPostId: state.firstChatbotAdReferral?.postId,
            $firstEntryAdAssetId: state.firstChatbotAdReferral?.assetId,
            $firstEntryAdCreative: state.firstChatbotAdReferral?.adCreativeName,
            $firstEntryAdSet: state.firstChatbotAdReferral?.adSetName,
            $firstEntryAdCampaign: state.firstChatbotAdReferral?.adCampaignName,
            $firstEntryChatbotPlatform: state.platform,

            $lastEntryAdId: state.lastChatbotAdReferral?.adId,
            $lastEntryAdTitle: state.lastChatbotAdReferral?.adTitle,
            $lastEntryAdPostId: state.lastChatbotAdReferral?.postId,
            $lastEntryAdAssetId: state.lastChatbotAdReferral?.assetId,
            $lastEntryAdCreative: state.lastChatbotAdReferral?.adCreativeName,
            $lastEntryAdSet: state.lastChatbotAdReferral?.adSetName,
            $lastEntryAdCampaign: state.lastChatbotAdReferral?.adCampaignName,
            $lastEntryChatbotPlatform: state.platform,

            $chatbotPlayMessageCount: state.chatbotPlayMessageCount,
            $chatbotMessageSinceLastSession: state.chatbotSentSinceEntry,
            $chatbotMessageInboundLastSession: state.chatbotReceivedSinceEntry,

            $chatbotDaysElapsedInboundMessage: state.chatbotLastReceivedTime
              ? (Date.now() - state.chatbotLastReceivedTime) /
                (1000 * 60 * 60 * 24)
              : undefined,
          },
        },
      ]);
    }

    async function sendPlayButtonMessage(
      opts?: PlayButtonOptions,
    ): Promise<void> {
      trackEvent('DebugSendPlayButtonMessageCalled', { opts });
      let token = '';

      try {
        token = await createLoginToken(api);
        trackEvent('DebugSendPlayButtonMessageLoginTokenCreated', {
          opts,
          token,
        });
      } catch (error) {
        trackEvent('DebugSendPlayButtonMessageLoginTokenFailed', {
          opts,
          error,
        });
        throw error;
      }

      // Channel should be "AD" only if we had an ad referral within the last 15 seconds
      const channel =
        state.lastChatbotAdReferral != null &&
        Date.now() - state.lastChatbotAdReferral.time < 15000
          ? 'AD'
          : 'CHATBOT';

      let payload: any = {
        $channel: channel,
        feature: 'webhook',
        subFeature: null,
        $loginToken: {
          userId: state.id,
          token,
        },
      };

      if (channel === 'AD') {
        payload.$adCreativeName = state.lastChatbotAdReferral?.adCreativeName;
        payload.$adSetName = state.lastChatbotAdReferral?.adSetName;
        payload.$adCampaignName = state.lastChatbotAdReferral?.adCampaignName;
      }

      // Extend with custom payload
      payload = {
        ...payload,
        ...opts?.payload,
      };

      const url = generateChatbotUrl(
        state.platform,
        payload,
        process.env.AMPLITUDE,
        state.id,
      );

      trackEvent('DebugWebhookSendPlayButtonMessage', {
        id: state.id,
        opts,
        token,
        url,
        payload,
      });

      const response = chatbotMessageTemplates.messengerPlayButton({
        args: {
          title: opts?.messageText ?? 'Ready to play Trembly?? 🐱',
          ...(opts?.messageSubtitle && { subtitle: opts?.messageSubtitle }),
          cta: opts?.buttonText ?? 'Play Now!',
          imageUrl: opts?.imageUrl,
          url,
        },
        payload: payload as any,
      });

      // Update properties for analytics
      state.chatbotPlayMessageCount += 1;
      state.chatbotSentSinceEntry += 1;
      state.chatbotLastSentTime = Date.now();

      if (state.platform === 'instagram') {
        api.chatbot.sendInstagramMessage(state.id, response);
      } else if (state.platform === 'messenger') {
        api.chatbot.sendMessengerMessage(state.id, response);
      } else {
        trackEvent('DebugWebhookSendPlayButtonMessageFailed', {
          id: state.id,
          opts,
          token,
          url,
          payload,
        });
        throw new Error('Assert');
      }

      // Ping Amplitude and update user properties
      trackEvent('ChatbotSentPlayButton');
    }

    async function storeAdReferral(referral: any, adAccountId?: string) {
      let adCreativeName: string | undefined;
      let adSetName: string | undefined;
      let adCampaignName: string | undefined;

      // Grab ad creative/adset/campaign names by looking up from the ad title (creative name)
      if (adAccountId && referral.ads_context_data?.ad_title) {
        // https://developers.facebook.com/docs/marketing-api/insights/parameters/v17.0
        const query = new URLSearchParams();
        query.set(
          'fields',
          ['name', 'adset{name}', 'campaign{name}'].join(','),
        );
        query.set(
          'filtering',
          JSON.stringify([
            {
              field: 'name',
              operator: 'EQUAL',
              value: referral.ads_context_data.ad_title,
            },
          ]),
        );
        query.set('limit', '1');

        // Temporary access token with ads_read access to the Trembly IG ad account
        const fb = new FBClient(
          'EAAT7SZCMlGEgBAFzcRtgtW6lZCCWkqg52xaK4Bsj9oeE7kZA3Iri71yzTZAsNPB0VCPSZBXVK0cWhLZADnGuSJP4mO5DXB2H07tchAteZBgtbQjH3ZCjsN6KLklUhGPeO6trfL4ZCzq0ZAQlMMrihNbuLh4g6t4cr2IXbsw8fZB81UtxgZDZD',
        );

        const res = await fb.get(`act_${adAccountId}/ads?${query.toString()}`);
        const ad = res['data']?.[0];
        adCreativeName = ad?.name;
        adSetName = ad?.adset.name;
        adCampaignName = ad?.campaign.name;
      }

      const info = {
        adId: referral.ad_id,
        time: Date.now(),
        adTitle: referral.ads_context_data?.ad_title,
        postId: referral.ads_context_data?.post_id,
        adCreativeName,
        adSetName,
        adCampaignName,
      };
      // If we don't have a previous referral, or we do but it doesn't have an ad ID
      if (state.firstChatbotAdReferral?.adId == null) {
        state.firstChatbotAdReferral = info;
      }
      state.lastChatbotAdReferral = info;

      // Ping Amplitude and update user properties
      trackEvent('ChatbotAdReferral', { implicit: false });
    }

    // Handle m.me ref URL params
    async function onLinkReferral(ref: string) {
      let refData: LinkRefData;
      try {
        refData = JSON.parse(decode(ref));
      } catch (error) {
        console.warn('Error parsing link referral ref', error);
      }

      trackEvent('ChatbotLinkEntry', { refData });

      let opts: PlayButtonOptions = {
        payload: {},
      };

      if (refData != null) {
        // according to shareLinkToMessenger() payload details
        opts.messageText = refData.messageText;
        opts.buttonText = refData.buttonText;
        opts.imageUrl = refData.imageUrl;
        opts.payload = refData.payload;

        // Track ShareConversion for the original sharing player
        const sourcePlayerId = refData.payload?.['playerID'] as string;
        if (sourcePlayerId) {
          api.sendAnalyticsEvents([
            {
              userId: sourcePlayerId,
              eventType: 'ShareConversion',
              eventProperties: refData.payload,
            },
          ]);
        }
      }

      await sendPlayButtonMessage(opts);
    }

    if (isInstagramWebhookEvent(event)) {
      state.platform = event.platform;
      state.instagramId =
        (event.sender as any)?.id || (event.value as any).from?.id; // messages or comments

      const message: any = event.message;

      if (message?.is_echo) {
        return; // Ignore echoes
      }

      const referral = message?.referral;
      if (referral?.source === 'ADS') {
        await storeAdReferral(referral, '764863718500500'); // Trembly IG ad account
      } else if (
        message?.reply_to?.story != null ||
        message?.attachments != null
      ) {
        // Hack: If this is a story reply or attachment, assume that this is part of an ad entry and
        // save a placeholder referral to the user state, without clobbering any previously saved
        // referrals.

        const assetId =
          message?.reply_to?.story?.id ??
          extractAssetIdFromAttachments(message?.attachments);
        const info = {
          time: Date.now(),
          assetId,
        };
        if (state.firstChatbotAdReferral?.adId == null) {
          state.firstChatbotAdReferral = info;
        }
        if (state.lastChatbotAdReferral?.adId == null) {
          state.lastChatbotAdReferral = info;
        }

        trackEvent('ChatbotAdReferral', { implicit: true });
      }

      // https://developers.facebook.com/docs/messenger-platform/instagram/features/ig-me-links/#refparams
      if ((event.postback as any)?.referral?.ref != null) {
        // New thread + icebreaker
        await onLinkReferral((event.postback as any).referral.ref);
      } else if (message?.referral?.ref != null) {
        // Existing conversation thread. Not currently working, we never get referral payloads for
        // existing Instagram conversations...
        await onLinkReferral(message.referral.ref);
      } else if ((event.message as any)?.quick_reply?.payload) {
        // Handle quick reply payloads
        let opts = parseButtonOpts((event.message as any).quick_reply.payload);
        opts = await processQuickReplyAction(state, api, opts);
        await sendPlayButtonMessage(opts);
      } else if ((event.postback as any)?.payload) {
        // Handle postbac payloads
        let opts = parseButtonOpts((event.postback as any).payload);
        opts = await processQuickReplyAction(state, api, opts);
        await sendPlayButtonMessage(opts);
      } else if (event.field === 'comments' && (event.value as any)?.id) {
        trackEvent('DebugInstagramComment', {});
        // comment2play flow
        await sendPrivateReplyMessage(
          state.platform,
          (event.value as any)?.id,
          api,
        );
      } else if (message?.text != null || event.postback != null) {
        // Plain play button
        await sendPlayButtonMessage();
      }
    } else if (isMessengerWebhookEvent(event)) {
      state.platform = event.platform;

      // Sender can be undefined here, as can be seen in sentry logs:
      // https://playco.sentry.io/issues/4191658440/?environment=production&project=4504851572260864&query=is%3Aunresolved&referrer=issue-stream&statsPeriod=14d&stream_index=22
      // The messengerId is not being used at the moment, so it is safe to leave it empty for now if that happens.
      if (event.sender && event.sender['id']) {
        state.messengerId = event.sender['id'];
      }

      const referral: any = event.referral;
      if (referral?.source === 'ADS') {
        await storeAdReferral(referral);
      }

      if (referral?.ref != null) {
        // Handle referrals to existing conversations
        await onLinkReferral(referral.ref);
      } else if ((event.postback as any)?.referral?.ref != null) {
        // Handle referrals to new conversations
        await onLinkReferral((event.postback as any).referral.ref);
      } else if ((event.message as any)?.quick_reply?.payload) {
        // Handle quick reply payloads (from Messenger JSON ads)
        let opts = parseButtonOpts((event.message as any).quick_reply.payload);
        opts = await processQuickReplyAction(state, api, opts);
        await sendPlayButtonMessage(opts);
      } else if ((event.postback as any)?.payload) {
        // Handle postbac payloads
        let opts = parseButtonOpts((event.postback as any).payload);
        opts = await processQuickReplyAction(state, api, opts);
        await sendPlayButtonMessage(opts);
      } else if (event.field === 'feed' && (event.value as any)?.comment_id) {
        // comment2play flow
        await sendPrivateReplyMessage(
          state.platform,
          (event.value as any)?.comment_id,
          api,
        );
      } else if (event.message != null || event.postback != null) {
        // Plain play button
        await sendPlayButtonMessage();
      }
    }

    // After everything else, track a ChatbotInboundMessage event with updated user props
    if (isMessengerWebhookEvent(event) || isInstagramWebhookEvent(event)) {
      if (!state.entryFinalCount) {
        // Force assign quiz egg chat test to this user
        (api.abTests as any).assign(ab.tests.TEST_QUIZ_EGG_CHAT);
        const bucket = getDynamicBucketID(api, ab.tests.TEST_QUIZ_EGG_CHAT);
        // Schedule quiz egg chat if user has not seen basic gameplay tutorial yet
        if (bucket === 'enabled') {
          scheduleQuizEggChat(api);
        }
      }

      if (event.message != null) {
        // Update properties for analytics
        state.chatbotReceivedSinceEntry += 1; // eslint-disable-line require-atomic-updates
        state.chatbotLastReceivedTime = Date.now(); // eslint-disable-line require-atomic-updates

        trackEvent('ChatbotInboundMessage', {
          platform: event.platform,
          text: (event.message as any).text,
        });
      } else if (
        event.field === 'comments' ||
        (event.field === 'feed' && (event.value as any)?.comment_id)
      ) {
        trackEvent('PageComment', {
          platform: event.platform,
          text: (event.value as any).text || (event.value as any).message, // Intagram || Facebook
          media_id: (event.value as any).media?.id, // Instagram
          post_id: (event.value as any).post_id, // Facebook
        });
      }
    }
  },
});

async function sendPrivateReplyMessage(
  platform: string,
  commentId: string,
  api: any,
): Promise<void> {
  let payload = {
    $channel: 'COMMENT',
    feature: 'webhook',
    subFeature: null,
  };

  const response = chatbotMessageTemplates.comment2play({
    args: {
      text: 'Hello! Wanna play together? 🐱',
    },
    payload: payload as any,
  });

  if (platform === 'instagram') {
    api.chatbot.sendInstagramMessage(api.getUserID(), response, { commentId });
  } else if (platform === 'messenger') {
    api.chatbot.sendMessengerMessage(api.getUserID(), response); // FIXME: recipient comment_id should be supported by the API
  } else {
    throw new Error('Assert');
  }

  // Ping Amplitude and update user properties
  api.sendAnalyticsEvents([
    {
      userId: api.getUserID(),
      eventType: 'ChatbotPrivateReplySent',
      eventProperties: payload,
      userProperties: {},
    },
  ]);
}

function isMessengerWebhookEvent(
  event: unknown,
): event is MessengerWebhookEvent {
  return (
    event &&
    typeof event === 'object' &&
    'platform' in event &&
    (event as any).platform === 'messenger'
  );
}

function isInstagramWebhookEvent(
  event: unknown,
): event is InstagramWebhookEvent {
  return (
    event &&
    typeof event === 'object' &&
    'platform' in event &&
    (event as any).platform === 'instagram'
  );
}

const chatbot = createChatbotConfig(chatbotMessageTemplates, events);

export default chatbot;

function parseButtonOpts(quickReplyPayload: string) {
  const quickReply = decodeQuickReplyPayload(quickReplyPayload);
  let opts: PlayButtonOptions = {
    messageText: quickReply?.messageText,
    ...(quickReply?.messageSubtitle && {
      messageSubtitle: quickReply?.messageSubtitle,
    }),
    imageUrl: quickReply?.imageUrl
      ? decodeURIComponent(quickReply.imageUrl)
      : undefined,
    buttonText: quickReply?.buttonText,
    action: quickReply?.action,
    species: quickReply?.species,
    payload: quickReply?.payload || {},
  };
  // Grab the ?payload param from the original button URL and use it to extend our final payload
  if (quickReply?.buttonUrl) {
    try {
      const buttonUrlPayload = new URL(quickReply.buttonUrl).searchParams.get(
        'payload',
      ); // note: automatically does decodeURIComponent()
      if (buttonUrlPayload) {
        opts.payload = { ...opts.payload, ...JSON.parse(buttonUrlPayload) };
      }
    } catch (error) {
      console.warn('Error parsing QuickReplyPayload.buttonUrl');
    }
  }

  return opts;
}
